import { useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import PublicFooter from '../components/PublicFooter';
import {
  BookOpen, CheckCircle2, BarChart3, ShieldCheck,
  ArrowRight, Check, Zap, Brain, Shield,
} from 'lucide-react';

const CONTENT = {
  bookkeeping: {
    title: 'Bookkeeping',
    eyebrow: 'AI-powered transaction engine',
    headline: 'The bookkeeping brain your firm deserves.',
    lede: 'SnuggleMind combines GPT-5.2 pattern recognition with a firm-specific context store to categorize every transaction the way your senior accountant would — only faster.',
    icon: BookOpen,
    color: '#0A3622',
    features: [
      { title: 'Context-aware categorization', desc: 'Our hybrid AI blends LLM reasoning with your accounting policies and vendor history.', icon: Brain },
      { title: 'Review queue that learns', desc: 'Every correction teaches the engine. The more you use it, the smarter it gets.', icon: Zap },
      { title: 'Multi-entity ready', desc: 'Scale from one client to hundreds without losing accuracy.', icon: Check },
    ],
    metrics: [
      { value: '92%', label: 'auto-categorized on import' },
      { value: '14×', label: 'faster than manual review' },
      { value: '0', label: 'rules to maintain' },
    ],
  },
  close: {
    title: 'Close',
    eyebrow: 'Month-end, engineered',
    headline: 'Close with confidence in days, not weeks.',
    lede: 'The Recon Center, Exception Queue, and Close Readiness Dashboard surface every blocker before it becomes a problem — so you ship clean books on time.',
    icon: CheckCircle2,
    color: '#D98A4A',
    features: [
      { title: 'Close Readiness scoring', desc: 'A live score tells you exactly what\'s blocking close and who owns it.', icon: BarChart3 },
      { title: 'Recon Center', desc: 'Reconcile bank, credit card, and clearing accounts in one unified workspace.', icon: Check },
      { title: 'Exception Queue', desc: 'Auto-flagged anomalies with AI-generated explanations and suggested fixes.', icon: Brain },
    ],
    metrics: [
      { value: '4 days', label: 'average close time' },
      { value: '87%', label: 'exceptions auto-resolved' },
      { value: '100%', label: 'audit-ready trail' },
    ],
  },
  reporting: {
    title: 'Reporting',
    eyebrow: 'Statements that explain themselves',
    headline: 'Real financial insights, not just numbers.',
    lede: 'Generate P&L, Balance Sheet, Trial Balance, and budget-vs-actual reports in seconds — each one rendered as pixel-perfect HTML-to-PDF and explained in plain English by SnuggleMind AI.',
    icon: BarChart3,
    color: '#0A3622',
    features: [
      { title: 'HTML-to-PDF statements', desc: 'Client-ready P&L, BS, and Trial Balance exports, styled to your firm\'s brand.', icon: Check },
      { title: 'Budget vs Actual', desc: 'Variance analysis with AI narrative explaining why the numbers moved.', icon: Brain },
      { title: 'Multi-currency', desc: 'Live rates via Frankfurter + FX gain/loss calculated automatically.', icon: Zap },
    ],
    metrics: [
      { value: '< 3s', label: 'to generate a P&L' },
      { value: '15+', label: 'report templates' },
      { value: '12', label: 'base currencies' },
    ],
  },
  controls: {
    title: 'Controls',
    eyebrow: 'Trust, verified',
    headline: 'Controls that actually control.',
    lede: 'Credit card tracking, missing-document chasers, audit trails, and MFA — every control your partner-level reviewers demand, without the spreadsheet gymnastics.',
    icon: ShieldCheck,
    color: '#D98A4A',
    features: [
      { title: 'Credit Card Controls', desc: 'Track every card, statement, and unreconciled charge across entities.', icon: Shield },
      { title: 'Missing Documents Chaser', desc: 'Auto-detect missing receipts and nudge clients until they arrive.', icon: Zap },
      { title: 'Full audit trail', desc: 'Every AI decision, override, and correction is logged forever.', icon: Check },
    ],
    metrics: [
      { value: '100%', label: 'decisions logged' },
      { value: 'MFA', label: 'on every login' },
      { value: 'SOC 2', label: 'ready architecture' },
    ],
  },
};

export default function SolutionPage() {
  const { slug } = useParams();
  const data = CONTENT[slug];

  // Stable cross-sell list: skip current slug, only recompute when slug changes.
  // Must be called BEFORE any conditional return per rules-of-hooks.
  const otherSolutions = useMemo(
    () => Object.entries(CONTENT).filter(([s]) => s !== slug),
    [slug]
  );

  if (!data) return <Navigate to="/" replace />;

  const Icon = data.icon;

  return (
    <div className="min-h-screen" style={{ background: '#F7F6F2' }} data-testid={`solution-page-${slug}`}>
      <PublicHeader />

      {/* HERO */}
      <section className="pt-20 pb-16 lg:pt-28 lg:pb-20 relative overflow-hidden">
        <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.24em] font-semibold mb-5" style={{ color: '#D98A4A' }} data-testid="solution-eyebrow">
              {data.eyebrow}
            </p>
            <h1
              className="font-heading font-semibold tracking-[-0.03em] mb-6"
              style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)', color: '#0A3622', lineHeight: 1 }}
              data-testid="solution-headline"
            >
              {data.headline}
            </h1>
            <p className="text-[18px] leading-relaxed max-w-2xl mb-8" style={{ color: '#4B5563' }}>{data.lede}</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register" className="inline-flex items-center gap-2 h-12 px-7 rounded-full text-[14px] font-body font-semibold transition-all hover:scale-[1.02]" style={{ background: '#D98A4A', color: '#FFFFFF' }} data-testid="solution-cta-trial">
                Start Free Trial <ArrowRight size={16} />
              </Link>
              <Link to="/pricing" className="inline-flex items-center gap-2 h-12 px-7 rounded-full text-[14px] font-body font-semibold" style={{ background: 'transparent', color: '#0A3622', border: '1.5px solid #0A3622' }}>
                View pricing
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 flex justify-center">
            <div className="relative">
              <div className="w-64 h-64 rounded-[40px] flex items-center justify-center" style={{ background: data.color, boxShadow: '0 30px 80px -20px rgba(10,54,34,0.3)' }}>
                <Icon size={110} className="text-white" strokeWidth={1.2} />
              </div>
              <div className="absolute -bottom-5 -left-5 w-24 h-24 rounded-3xl" style={{ background: data.color === '#D98A4A' ? '#0A3622' : '#D98A4A', opacity: 0.15 }} />
              <div className="absolute -top-5 -right-5 w-16 h-16 rounded-2xl" style={{ background: '#D98A4A', opacity: 0.25 }} />
            </div>
          </div>
        </div>
      </section>

      {/* METRICS */}
      <section className="py-12 border-y" style={{ borderColor: '#E5E3DB', background: '#FFFFFF' }}>
        <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
          {data.metrics.map((m) => (
            <div key={m.label} className="text-center">
              <p className="font-heading font-semibold tracking-tight mb-1" style={{ fontSize: 'clamp(2.5rem, 4vw, 3.5rem)', color: '#D98A4A', lineHeight: 1 }}>{m.value}</p>
              <p className="text-[14px] font-body" style={{ color: '#4B5563' }}>{m.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-24">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="mb-14 max-w-2xl">
            <h2 className="font-heading font-semibold tracking-tight mb-4" style={{ fontSize: 'clamp(2rem, 3.5vw, 2.75rem)', color: '#0A3622', lineHeight: 1.1 }}>
              Purpose-built for modern firms.
            </h2>
            <p className="text-[17px] leading-relaxed" style={{ color: '#4B5563' }}>
              Every feature engineered to eliminate the grunt work that eats up your team's week.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {data.features.map((f) => (
              <div key={f.title} className="p-7 rounded-2xl" style={{ background: '#FFFFFF', border: '1px solid #E5E3DB' }}>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{ background: 'rgba(217,138,74,0.12)' }}>
                  <f.icon size={22} style={{ color: '#D98A4A' }} />
                </div>
                <h3 className="font-heading text-[18px] font-semibold mb-2.5" style={{ color: '#0A3622' }}>{f.title}</h3>
                <p className="text-[14px] leading-relaxed" style={{ color: '#4B5563' }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CROSS-SELL: OTHER SOLUTIONS */}
      <section className="pb-24">
        <div className="max-w-[1280px] mx-auto px-6">
          <h3 className="font-heading font-semibold tracking-tight mb-8" style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2rem)', color: '#0A3622' }}>
            Explore the rest of the platform
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {otherSolutions.map(([s, d]) => (
              <Link
                key={s}
                to={`/solutions/${s}`}
                className="group p-5 rounded-2xl flex items-center gap-4 transition-all hover:-translate-y-0.5"
                style={{ background: '#FFFFFF', border: '1px solid #E5E3DB' }}
                data-testid={`cross-sell-${s}`}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(217,138,74,0.12)' }}>
                  <d.icon size={19} style={{ color: '#D98A4A' }} />
                </div>
                <div className="flex-1">
                  <p className="font-heading text-[15px] font-semibold" style={{ color: '#0A3622' }}>{d.title}</p>
                </div>
                <ArrowRight size={15} className="opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: '#D98A4A' }} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24" style={{ background: '#0A3622' }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading font-semibold tracking-tight text-white mb-6" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1 }}>
            Ready to put {data.title.toLowerCase()} on autopilot?
          </h2>
          <Link to="/register" className="inline-flex items-center gap-2 h-12 px-8 rounded-full text-[15px] font-body font-semibold transition-all hover:scale-[1.02]" style={{ background: '#D98A4A', color: '#FFFFFF' }} data-testid="solution-final-cta">
            Start Free Trial <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
